import { useRef, useCallback, useEffect } from "react";
import api from "../services/api";
import { useVoicePreviewAudio } from "./useVoicePreviewAudio";

const GPT4O_ONLY_VOICES = new Set(["ballad", "verse", "marin", "cedar"]);

export const getEffectiveTtsModel = (voice, ttsModel) => {
  const normalizedVoice = String(voice || "").toLowerCase();
  const model = ttsModel || "tts-1";
  if (GPT4O_ONLY_VOICES.has(normalizedVoice) && (model === "tts-1" || model === "tts-1-hd")) {
    return "gpt-4o-mini-tts";
  }
  return model;
};

const parsePreviewError = async (err) => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return null;
  }

  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      return json?.error || json?.message || err?.message;
    } catch (_) {}
  }

  return err?.response?.data?.error || err?.message || "Falha ao gerar prévia.";
};

const buildPreviewBody = (payload) => ({
  ...payload,
  ttsModel: getEffectiveTtsModel(payload.voice, payload.ttsModel),
});

const triggerBlobDownload = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName || "voz-teste.mp3";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => {
    try {
      URL.revokeObjectURL(url);
    } catch (_) {}
  }, 2000);
};

const payloadKey = (payload) =>
  JSON.stringify({
    text: payload?.text,
    ttsProvider: payload?.ttsProvider,
    voice: payload?.voice,
    voiceKey: payload?.voiceKey,
    voiceRegion: payload?.voiceRegion,
    ttsModel: getEffectiveTtsModel(payload?.voice, payload?.ttsModel),
    openaiTtsInstructions: payload?.openaiTtsInstructions,
    ttsSpeed: payload?.ttsSpeed,
  });

export const useTtsPreview = () => {
  const { playBlob, stop, release } = useVoicePreviewAudio();
  const abortRef = useRef(null);
  const lastBlobRef = useRef(null);
  const lastPayloadKeyRef = useRef("");

  useEffect(() => () => {
    abortRef.current?.abort();
    release();
    lastBlobRef.current = null;
    lastPayloadKeyRef.current = "";
  }, [release]);

  const fetchPreviewBlob = useCallback(async (payload, { signal } = {}) => {
    const response = await api.post("/tts/preview", buildPreviewBody(payload), {
      responseType: "blob",
      signal,
    });
    return response.data;
  }, []);

  const playPreview = useCallback(async (payload) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const blob = await fetchPreviewBlob(payload, { signal: controller.signal });

      if (controller.signal.aborted) {
        return false;
      }

      lastBlobRef.current = blob;
      lastPayloadKeyRef.current = payloadKey(payload);
      await playBlob(blob);
      return true;
    } catch (err) {
      const message = await parsePreviewError(err);
      if (message === null) {
        return false;
      }
      throw new Error(message);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [fetchPreviewBlob, playBlob]);

  const downloadPreview = useCallback(async (payload, fileName) => {
    const key = payloadKey(payload);
    let blob = null;

    if (lastBlobRef.current && lastPayloadKeyRef.current === key) {
      blob = lastBlobRef.current;
    } else {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        blob = await fetchPreviewBlob(payload, { signal: controller.signal });
        if (controller.signal.aborted) {
          return false;
        }
        lastBlobRef.current = blob;
        lastPayloadKeyRef.current = key;
      } catch (err) {
        const message = await parsePreviewError(err);
        if (message === null) {
          return false;
        }
        throw new Error(message);
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    }

    const safeVoice = String(payload?.voice || "voz")
      .replace(/[^\w\-]+/g, "_")
      .slice(0, 40);
    triggerBlobDownload(
      blob,
      fileName || `voz-teste-${safeVoice}.mp3`
    );
    return true;
  }, [fetchPreviewBlob]);

  return {
    playPreview,
    downloadPreview,
    stopPreview: stop,
    releasePreview: release,
    getEffectiveTtsModel,
  };
};
